import React from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { TapeBuilderForImplementation } from '~/packages/tapeBuilder/ui/TapeBuilderForImplementation';

/**
 * Pipeline Config (tape-builder) section of the BUS deal Settings hub.
 *
 * **Admin-only.** The tape-builder editor has no read-only mode (editing the
 * transform graph directly affects ingestion + reconcile), so non-admin backup
 * users (roleType MEMBER / CONTRIBUTOR) are bounced to the deal overview rather
 * than shown a view they cannot safely use. (The hub also hides the section and
 * omits the route for non-admins; this guard is defense-in-depth.)
 *
 * Reuses the same `TapeBuilderForImplementation` the core `/implementations`
 * surface uses, in customer mode (`adminMode={false}` — hides the internal
 * asset-verification panel). The deal's graph is resolved from the
 * implementation's `borrowerPipelineDefinitionId`. The hub provides page padding.
 */
export const DealTapeBuilderView = (): React.ReactElement => {
  const { user } = useUserAuthOrThrow();
  const { implementationId } = useParams();

  if (!implementationId) return <Navigate to="/portfolio" replace />;

  // Tape Builder is admin-only (no read-only mode); bounce non-admins.
  if (user.roleType !== 'ADMIN') {
    return <Navigate to={`/deals/${implementationId}`} replace />;
  }

  return (
    <TapeBuilderForImplementation
      implementationId={implementationId}
      implementationRoutePrefix="/deals/"
      adminMode={false}
    />
  );
};
